import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  PlayArrow as PlayArrowIcon,
} from '@mui/icons-material';
import type {
  ReservationSlotTemplate,
  CategoryCounts,
  AssignmentOrigin,
} from '../../../../domain/reservationSlots';
import {
  TREATMENT_CATEGORIES,
  resolveAssignmentOrigin,
  summarizeIntervalCategories,
} from '../../../../domain/reservationSlots';
import type { UseReservationSlotGenerationReturn } from '../hooks/useReservationSlotGeneration';
import { formatYearMonthToSlash } from '../../../formatters';
import CategoryChip from './CategoryChip';
import {
  MonthNavigation,
  LoadingOverlay,
  PanelActionBar,
  CategoryCountWrapper,
  SlotPreviewRow,
} from '../ReservationSlotManagement.styles';

/**
 * GenerationPanel のProps型
 *
 * @property {ReservationSlotTemplate[]} templates - 選択可能なテンプレート一覧
 * @property {UseReservationSlotGenerationReturn} generation - 解放枠生成フックの戻り値
 * @property {boolean} [embedded] - 月スコープの設定ビューに埋め込む場合に true。月ナビゲーションを隠す
 * @property {function} [onCancel] - キャンセルボタン押下時のコールバック。未指定時はボタンを表示しない
 */
export interface GenerationPanelProps {
  templates: ReservationSlotTemplate[];
  generation: UseReservationSlotGenerationReturn;
  embedded?: boolean;
  onCancel?: () => void;
}

/**
 * 解放枠管理画面のアクセントカラー
 *
 * @description
 * HTMLモックの配色をそのまま用いる。MUIのセマンティックカラーでは
 * 「適用元」の意味（基本設定/個別上書き/手動変更）と対応しないため直接指定する。
 */
const PANEL_COLORS = {
  blue: '#2D6CDF',
  blueSoft: '#EAF1FE',
  blueDark: '#1A4FB0',
  borderStrong: '#C8CDD8',
  green: '#1C9A5B',
  red: '#D14343',
  gray: '#8A8F98',
  graySoft: '#EEF0F2',
  amber: '#B9740A',
  amberSoft: '#FBF1DC',
} as const;

/**
 * 適用元バッジの表示定義
 *
 * @description
 * キーの無い適用元（generated / unresolved）はバッジを表示しない。
 * 生成済み・未生成は「状態」列で示しているため重複表示を避ける。
 */
const ORIGIN_BADGES: Partial<
  Record<AssignmentOrigin, { label: string; background: string; color: string }>
> = {
  manual: { label: '手動変更', background: PANEL_COLORS.graySoft, color: PANEL_COLORS.gray },
  override: { label: '個別上書き', background: PANEL_COLORS.amberSoft, color: PANEL_COLORS.amber },
  default: { label: '基本設定', background: PANEL_COLORS.blueSoft, color: PANEL_COLORS.blueDark },
};

/**
 * 一括変更の適用結果メッセージの表示時間（ミリ秒）
 */
const BULK_MESSAGE_DURATION_MS = 2500;

/**
 * 一括変更のモード
 *
 * - reset:    手動変更を破棄し基本設定・個別上書きの割り当てに戻す
 * - template: 選択したテンプレートを全行へ適用する
 * - 空文字:   未選択
 */
type BulkMode = '' | 'reset' | 'template';

/**
 * 一括変更の適用結果メッセージ
 *
 * @property {string} text - 表示する文言
 * @property {'success' | 'error'} severity - 成功（緑）/ エラー（赤）
 */
interface BulkMessage {
  text: string;
  severity: 'success' | 'error';
}

/**
 * 一括変更の選択肢カードのスタイルを組み立てる
 *
 * @param {boolean} selected - 選択中かどうか
 * @returns {object} sxプロパティ
 */
const bulkOptionSx = (selected: boolean) => ({
  border: '1.5px solid',
  borderColor: selected ? PANEL_COLORS.blue : PANEL_COLORS.borderStrong,
  backgroundColor: selected ? PANEL_COLORS.blueSoft : 'transparent',
  borderRadius: 2,
  px: 1.5,
  py: 1.25,
  transition: 'border-color .12s, background-color .12s',
});

/**
 * 適用元バッジ
 *
 * @description
 * 「そのテンプレートがどこから来たのか」を色付きバッジで示す。
 * 生成済み（generated）・未解決（unresolved）は表示対象外。
 *
 * @param {AssignmentOrigin} origin - 適用元の種別
 * @returns {React.ReactElement | null} バッジ（表示対象外の場合はnull）
 */
const OriginBadge: React.FC<{ origin: AssignmentOrigin }> = ({ origin }) => {
  const badge = ORIGIN_BADGES[origin];
  if (!badge) {
    return null;
  }
  return (
    <Box
      component="span"
      sx={{
        flexShrink: 0,
        display: 'inline-flex',
        alignItems: 'center',
        fontSize: 10,
        fontWeight: 700,
        lineHeight: 1.6,
        padding: '2px 7px',
        borderRadius: 999,
        whiteSpace: 'nowrap',
        backgroundColor: badge.background,
        color: badge.color,
      }}
    >
      {badge.label}
    </Box>
  );
};

/**
 * 生成結果のカテゴリ別枠数をチップ列で表示する
 *
 * @param {CategoryCounts} counts - カテゴリ別の枠数
 * @returns {React.ReactElement} カテゴリチップ列
 */
const GenerationResultCounts: React.FC<{ counts: CategoryCounts }> = ({ counts }) => {
  const entries = TREATMENT_CATEGORIES.filter((c) => (counts[c] ?? 0) > 0);
  if (entries.length === 0) {
    return <Typography variant="body2" color="text.secondary">—</Typography>;
  }
  return (
    <CategoryCountWrapper>
      {entries.map((category) => (
        <CategoryChip key={category} category={category} count={counts[category]} />
      ))}
    </CategoryCountWrapper>
  );
};

/**
 * 1区間あたりに生成される枠の構成を表示するセル
 *
 * @description
 * 交互パターンの場合は「ピル ×2 スキン ×1 / ピル ×2 ウェルクリ ×1」のように
 * A・Bパターンを「/」で区切って並べる。テンプレート未選択時は「—」を表示する。
 *
 * @param {ReservationSlotTemplate} [template] - 適用テンプレート（未選択・不明な場合はundefined）
 * @returns {React.ReactElement} 枠構成の表示
 */
const SlotPreviewCell: React.FC<{ template?: ReservationSlotTemplate }> = ({ template }) => {
  const summary = useMemo(
    () => (template ? summarizeIntervalCategories(template) : null),
    [template]
  );

  if (!summary) {
    return <Typography variant="body2" color="text.secondary">—</Typography>;
  }

  const countsB = summary.b;
  const entriesA = TREATMENT_CATEGORIES.filter((c) => (summary.a[c] ?? 0) > 0);
  const entriesB = countsB ? TREATMENT_CATEGORIES.filter((c) => (countsB[c] ?? 0) > 0) : [];

  if (entriesA.length === 0 && entriesB.length === 0) {
    return <Typography variant="body2" color="text.secondary">—</Typography>;
  }

  return (
    <SlotPreviewRow>
      {entriesA.map((category) => (
        <CategoryChip key={`a-${category}`} category={category} count={summary.a[category]} />
      ))}
      {countsB && entriesB.length > 0 && (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ px: 0.25 }}>
            /
          </Typography>
          {entriesB.map((category) => (
            <CategoryChip key={`b-${category}`} category={category} count={countsB[category]} />
          ))}
        </>
      )}
    </SlotPreviewRow>
  );
};

/**
 * 解放枠生成パネル
 *
 * @description
 * 対象月の確定済み医師シフトを一覧し、各シフトへ適用するテンプレートを選択して
 * 解放枠を一括生成する。各行には適用元（基本設定・個別上書き・手動変更）のバッジと
 * 生成される枠の構成を表示し、一括変更では「設定へ戻す」「全行へ適用」を選べる。
 *
 * 対象シフト一覧は「反映して一覧を表示」を押して一括変更を適用したあとに表示する。
 * 一覧を表示するまでは内容を確認できないため、一括生成ボタンも非活性にする。
 * ただし対象シフトが0件のときは一括変更自体が操作できないため、案内文より先に
 * 「対象シフトなし」を表示して操作できない理由がわかるようにする。
 *
 * 対象月は通常は本パネル内の月ナビゲーションで切り替えるが、`embedded` を指定した場合は
 * 遷移元で決まった月に固定されるため月ナビゲーションを描画しない。
 */
const GenerationPanel: React.FC<GenerationPanelProps> = ({
  templates,
  generation,
  embedded = false,
  onCancel,
}) => {
  const {
    year,
    month,
    targetShifts,
    assignments,
    manualShiftIds,
    overrideMap,
    defaultTemplateId,
    isLoading,
    generating,
    error,
    result,
    goToPreviousMonth,
    goToNextMonth,
    setAssignment,
    applyTemplateToAll,
    resetToSettings,
    generate,
    clearError,
    clearResult,
  } = generation;

  const [bulkMode, setBulkMode] = useState<BulkMode>('');
  const [bulkTemplateId, setBulkTemplateId] = useState<string>('');
  const [bulkMessage, setBulkMessage] = useState<BulkMessage | null>(null);
  // 対象シフト一覧は「反映して一覧を表示」を押すまで表示しない
  const [listVisible, setListVisible] = useState(false);
  // 生成完了メッセージの位置（一括生成ボタンから遠いため、完了時にここへスクロールする）
  const resultRef = useRef<HTMLDivElement>(null);

  // 適用結果メッセージは一定時間で自動的に消す
  useEffect(() => {
    if (!bulkMessage) {
      return undefined;
    }
    const timerId = setTimeout(() => setBulkMessage(null), BULK_MESSAGE_DURATION_MS);
    return () => clearTimeout(timerId);
  }, [bulkMessage]);

  // 一括生成ボタンは一覧の下端にあるため、完了メッセージを自動で視界に入れる
  useEffect(() => {
    if (!result) {
      return;
    }
    resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [result]);

  // templateId → テンプレートの引き当て用マップ
  const templateById = useMemo(
    () => new Map(templates.map((template) => [template.id, template])),
    [templates]
  );

  const bulkDisabled = isLoading || generating || targetShifts.length === 0;

  const handleApplyBulk = () => {
    if (bulkMode === '') {
      setBulkMessage({ text: '適用方法を選択してください', severity: 'error' });
      return;
    }
    if (bulkMode === 'reset') {
      resetToSettings();
      setListVisible(true);
      setBulkMessage({ text: '基本設定・個別上書きに戻しました', severity: 'success' });
      return;
    }
    if (!bulkTemplateId) {
      setBulkMessage({ text: 'テンプレートを選択してください', severity: 'error' });
      return;
    }
    applyTemplateToAll(bulkTemplateId);
    setListVisible(true);
    const templateName = templateById.get(bulkTemplateId)?.name ?? '';
    setBulkMessage({
      text: `全${targetShifts.length}行に「${templateName}」を適用しました`,
      severity: 'success',
    });
  };

  return (
    <Box sx={{ position: 'relative' }}>
      {(isLoading || generating) && (
        <LoadingOverlay>
          <CircularProgress />
        </LoadingOverlay>
      )}

      {/* 月ナビゲーション（埋め込み時は遷移元で月が確定しているため表示しない） */}
      {!embedded && (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <MonthNavigation>
            <IconButton onClick={goToPreviousMonth} disabled={isLoading || generating} size="small">
              <ChevronLeftIcon />
            </IconButton>
            <Typography
              variant="subtitle1"
              fontWeight={600}
              sx={{ minWidth: 110, textAlign: 'center' }}
            >
              {formatYearMonthToSlash(year, month)}
            </Typography>
            <IconButton onClick={goToNextMonth} disabled={isLoading || generating} size="small">
              <ChevronRightIcon />
            </IconButton>
          </MonthNavigation>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
          {error}
        </Alert>
      )}

      {result && (
        <Alert ref={resultRef} severity="success" sx={{ mb: 2 }} onClose={clearResult}>
          <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>
            {formatYearMonthToSlash(year, month)}の解放枠を{result.createdCount}件生成しました。
          </Typography>
          <GenerationResultCounts counts={result.categoryCounts} />
        </Alert>
      )}

      {/* 対象シフトが0件だと一括変更・一括生成が一切操作できないため、その理由を明示する */}
      {!isLoading && targetShifts.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          対象月に確定済みの医師シフトがありません。月次シフトのステータスが「確定」で、かつその月の
          医師シフトが登録されている必要があります。
        </Alert>
      )}

      {/* 一括変更 */}
      <Box
        sx={{
          border: '1px solid',
          borderColor: PANEL_COLORS.borderStrong,
          borderRadius: 2,
          p: 2,
          mb: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700, mb: 1.5 }}>
          一括変更
        </Typography>

        <RadioGroup
          value={bulkMode}
          onChange={(e) => setBulkMode(e.target.value as BulkMode)}
          sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}
        >
          {/* 選択肢A: 設定由来の割り当てに戻す */}
          <Box sx={bulkOptionSx(bulkMode === 'reset')}>
            <FormControlLabel
              value="reset"
              disabled={bulkDisabled}
              control={<Radio size="small" sx={{ py: 0, mt: 0.25 }} />}
              disableTypography
              sx={{ alignItems: 'flex-start', m: 0, width: '100%' }}
              label={
                <Box>
                  <Typography variant="body2" fontWeight={700}>
                    ↺ 基本設定・個別上書きに戻す
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    すべての手動変更をリセットし、基本設定・個別上書きの割り当てに戻します
                  </Typography>
                </Box>
              }
            />
          </Box>

          {/* 選択肢B: テンプレートを全行へ適用 */}
          <Box sx={bulkOptionSx(bulkMode === 'template')}>
            <FormControlLabel
              value="template"
              disabled={bulkDisabled}
              control={<Radio size="small" sx={{ py: 0, mt: 0.25 }} />}
              disableTypography
              sx={{ alignItems: 'flex-start', m: 0, width: '100%' }}
              label={
                <Box>
                  <Typography variant="body2" fontWeight={700}>
                    テンプレートを選んで全行に適用
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    選択したテンプレートをすべてのシフト行に一括で設定します
                  </Typography>
                </Box>
              }
            />
            <Box sx={{ pl: 3.75, pt: 1 }}>
              <FormControl size="small" fullWidth sx={{ maxWidth: 360 }}>
                <InputLabel>テンプレート</InputLabel>
                <Select
                  label="テンプレート"
                  value={bulkTemplateId}
                  disabled={bulkDisabled}
                  onChange={(e) => setBulkTemplateId(e.target.value)}
                  sx={{ backgroundColor: 'background.paper' }}
                >
                  {templates.map((template) => (
                    <MenuItem key={template.id} value={template.id}>
                      {template.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
        </RadioGroup>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1.5 }}>
          <Typography
            variant="caption"
            sx={{
              flex: 1,
              fontWeight: bulkMessage ? 700 : 400,
              color: bulkMessage
                ? bulkMessage.severity === 'success'
                  ? PANEL_COLORS.green
                  : PANEL_COLORS.red
                : 'text.disabled',
            }}
          >
            {bulkMessage ? bulkMessage.text : '適用後も行ごとに個別変更できます'}
          </Typography>
          <Button variant="contained" onClick={handleApplyBulk} disabled={bulkDisabled}>
            反映して一覧を表示
          </Button>
        </Box>
      </Box>

      {/* 対象シフト一覧（0件の場合はその旨を優先し、それ以外は「反映して一覧を表示」を押すまで案内文のみ） */}
      {targetShifts.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          対象月に確定済みの医師シフトがありません。
        </Typography>
      ) : !listVisible ? (
        <Box
          sx={{
            border: '1px dashed',
            borderColor: PANEL_COLORS.borderStrong,
            borderRadius: 2,
            py: 4,
            px: 2,
            textAlign: 'center',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            一括変更の内容を選び「反映して一覧を表示」を押すと、対象シフトの一覧が表示されます。
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 480 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, minWidth: 110 }}>日付</TableCell>
                <TableCell sx={{ fontWeight: 600, minWidth: 140 }}>医師</TableCell>
                <TableCell sx={{ fontWeight: 600, minWidth: 120 }}>勤務時間</TableCell>
                <TableCell sx={{ fontWeight: 600, minWidth: 260 }}>適用テンプレート</TableCell>
                <TableCell sx={{ fontWeight: 600, minWidth: 200 }}>生成される枠</TableCell>
                <TableCell sx={{ fontWeight: 600, textAlign: 'center', minWidth: 90 }}>状態</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {targetShifts.map((shift) => {
                const assignedTemplateId = assignments[shift.doctorShiftId];
                const assignedTemplate = assignedTemplateId
                  ? templateById.get(assignedTemplateId)
                  : undefined;
                const origin = resolveAssignmentOrigin({
                  doctorId: shift.doctorId,
                  sourceTemplateId: shift.sourceTemplateId,
                  isManual: manualShiftIds.has(shift.doctorShiftId),
                  overrideMap,
                  defaultTemplateId,
                });

                return (
                  <TableRow key={shift.doctorShiftId} hover>
                    <TableCell>{shift.date}</TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>{shift.doctorName}</TableCell>
                    <TableCell>
                      {shift.startTime}〜{shift.endTime}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <FormControl size="small" sx={{ flex: 1, minWidth: 160 }}>
                          <Select
                            value={assignedTemplate ? assignedTemplate.id : ''}
                            onChange={(e) => setAssignment(shift.doctorShiftId, e.target.value)}
                            displayEmpty
                          >
                            <MenuItem value="">
                              <em>未選択</em>
                            </MenuItem>
                            {templates.map((template) => (
                              <MenuItem key={template.id} value={template.id}>
                                {template.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <OriginBadge origin={origin} />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <SlotPreviewCell template={assignedTemplate} />
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      {shift.sourceTemplateId ? (
                        <Chip label="生成済み" size="small" color="primary" variant="outlined" />
                      ) : (
                        <Chip label="未生成" size="small" variant="outlined" />
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <PanelActionBar>
        {onCancel && (
          <Button variant="outlined" onClick={onCancel} disabled={generating}>
            キャンセル
          </Button>
        )}
        <Button
          variant="contained"
          startIcon={<PlayArrowIcon />}
          onClick={generate}
          disabled={generating || isLoading || targetShifts.length === 0 || !listVisible}
        >
          {embedded ? '解放枠を一括生成する' : '解放枠を生成'}
        </Button>
      </PanelActionBar>
    </Box>
  );
};

export default GenerationPanel;
